import Vue from "vue";
import Vuex from "vuex";
import jwtDecode from "jwt-decode";
import isAdmin from "../util/isAdmin";
// eslint-disable-next-line no-unused-vars
import * as StoreTypes from "../types/store";

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    username: "",
    userId: undefined,
    isAdmin: false,
    userRoles: [],
    notifications: 0,

    snackbar: {
      visible: false,
      text: "",
      timeout: 3000,
      icon: "mdi-alert-rhombus",
      iconColor: "ok",
      multiline: false,
    },

    reportPopup: {
      visible: false,
      contentId: undefined,
      contentType: "",
    },

    drawer: {
      visible: false,
      status: "",
      data: {},
    },

    search: {
      query: "",
      category: "",
      results: [],
      scrollY: 0,
    },

    homePage: {
      results: [],
      scrollY: 0,
      reviewSetIdx: 0,
      endOfReviews: false,
    },

    explorePage: {
      results: [],
      scrollY: 0,
      endOfResults: false,
      authenticatedUser: null,
      medium: null,
    },

    userWorkRelationshipPages: {
      /** @type {StoreTypes.UserWorkRelationshipPageState} */
      savedForLater: {
        page: null,
        routeUsername: null,
        filter: null,
        sortBy: {
          field: null,
          order: null,
        },
        results: {
          totalCount: null,
          items: [],
        },
        scrollY: null,
      },
      /** @type {StoreTypes.UserWorkRelationshipPageState} */
      engaged: {},
    },
  },

  mutations: {
    logout(state) {
      state.username = "";
      state.userId = undefined;
      state.isAdmin = false;
      state.notifications = 0;

      state.homePage = {
        results: [],
        scrollY: 0,
        reviewSetIdx: 0,
        endOfReviews: false,
      };

      return "Logged Out";
    },
    async login(state, payload) {
      let decoded;
      try {
        decoded = jwtDecode(payload.authorization);
      } catch (err) {
        return err;
      }
      state.username = decoded.username;
      state.userId = decoded.userId;
      state.isAdmin = await isAdmin(state.userId);
    },

    setUserRoles(state, payload) {
      state.userRoles = payload;
    },

    /*
      Notifications
    */
    setNotifications(state, payload) {
      if (!isNaN(parseInt(payload.count))) {
        state.notifications = parseInt(payload.count);
        return "Notifications set";
      } else {
        return new Error("Count not valid");
      }
    },
    decrementNotifications(state) {
      if (state.notifications > 0) {
        state.notifications -= 1;
      }
    },

    /*
      Snackbar
    */
    showSnackbar(state, payload) {
      state.snackbar.text = payload.text;
      state.snackbar.multiline = payload.text.length > 50;

      if (payload.multiline) {
        state.snackbar.multiline = payload.multiline;
      }

      if (payload.timeout) {
        state.snackbar.timeout = payload.timeout;
      }

      if (payload.error) {
        state.snackbar.iconColor = "error";
        state.snackbar.icon = "mdi-alert-rhombus";
      } else {
        state.snackbar.iconColor = "success";
        state.snackbar.icon = "mdi-check-circle";
      }

      state.snackbar.visible = true;
    },

    closeSnackbar(state) {
      state.snackbar.visible = false;
      state.snackbar.multiline = false;
      state.snackbar.timeout = 3000;
      state.snackbar.text = null;
    },

    showReportPopup(state, payload) {
      state.reportPopup.visible = true;
      state.reportPopup.contentId = payload.contentId;
      state.reportPopup.contentType = payload.contentType;
    },

    closeReportPopup(state) {
      state.reportPopup.visible = false;
      state.reportPopup.contentId = undefined;
    },

    /*
      Review Drawer
    */
    showDrawer(state, payload) {
      state.drawer.visible = true;
      state.drawer.status = payload.status;
      state.drawer.data = payload.data;
    },

    closeDrawer(state) {
      state.drawer.visible = false;
    },

    /*
      Persistent State Pages
    */
    saveSearch(state, payload) {
      state.search.query = payload.query;
      state.search.results = payload.results;
      state.search.category = payload.category;
      state.search.scrollY = payload.scrollY;
    },

    saveHomePage(state, payload) {
      state.homePage.results = payload.results;
      state.homePage.scrollY = payload.scrollY;
      state.homePage.reviewSetIdx = payload.reviewSetIdx;
      state.homePage.endOfReviews = payload.endOfReviews;
    },

    /**
     * Saves the state of the explore page to re-instate upon backwards navigation
     * @param {*} state
     * @param {StoreTypes.SaveExplorePagePayload} payload
     */
    saveExplorePage(state, payload) {
      state.explorePage.medium = payload.medium;
      state.explorePage.results = payload.results;
      state.explorePage.scrollY = payload.scrollY;
      state.explorePage.endOfResults = payload.endOfResults;
      state.explorePage.sortBy = payload.sortBy;
      state.explorePage.authenticatedUser = payload.authenticatedUser;
    },

    /**
     *
     * @param {*} state
     * @param {StoreTypes.SaveUserWorkRelationshipPagePayload} payload
     */
    saveUserWorkRelationshipPage(state, payload) {
      state.userWorkRelationshipPages[payload.interactionType] = payload.pageState;
    },

    /*
      User-Work interactions
    */
    updateSavedForLater(state, payload) {
      if (state.search.category === payload.medium) {
        state.search.results.forEach((item) => {
          if (item.id === payload.internal_id) {
            item.saved_for_later = payload.value;
          }
        });
      }
    },
    updateSeen(state, payload) {
      if (state.search.category === payload.medium) {
        state.search.results.forEach((item) => {
          if (item.id.toString() === payload.workId.toString()) {
            item.engaged = payload.value;
          }
        });
      }
    },
  },
});
